<template>
  <b-modal
    v-model="visible"
    title="Timeline do Participante"
    hide-footer
    centered
    size="md"
    @show="loadTimeline"
  >
    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <i class="bx bx-loader-alt bx-spin font-size-24 text-primary"></i>
      <p class="text-muted mt-2 mb-0">Carregando timeline...</p>
    </div>

    <!-- Content -->
    <div v-else-if="timeline">
      <!-- Participant header -->
      <div class="tl-header">
        <div class="tl-avatar">
          <i class="bx bx-user"></i>
        </div>
        <div class="tl-header-info">
          <span class="tl-header-name">{{ timeline.participant.name }}</span>
          <span class="tl-header-email">{{ timeline.participant.email }}</span>
        </div>
        <span class="tl-enrollment-status" :class="'tl-es-' + timeline.enrollment.status">
          {{ enrollmentStatusLabel(timeline.enrollment.status) }}
        </span>
      </div>

      <!-- Timeline -->
      <div class="tl-timeline">
        <!-- Executed steps -->
        <div
          v-for="(step, idx) in timeline.steps"
          :key="'step-' + step.log_id"
          class="tl-item"
          :class="{
            'tl-item-completed': step.status === 'completed',
            'tl-item-failed': step.status === 'failed',
            'tl-item-waiting': step.status === 'waiting' || step.status === 'pending',
            'tl-item-processing': step.status === 'processing',
            'tl-item-branch': !!step.parent_node_id,
            'tl-item-last': idx === timeline.steps.length - 1 && timeline.pending_nodes.length === 0
          }"
        >
          <div class="tl-dot-line">
            <div class="tl-dot" :class="'tl-dot-' + step.status">
              <i :class="stepIcon(step)"></i>
            </div>
            <div class="tl-line" v-if="idx < timeline.steps.length - 1 || timeline.pending_nodes.length > 0"></div>
          </div>
          <div class="tl-content">
            <div class="tl-content-head">
              <span class="tl-step-label">{{ step.node_label }}</span>
              <span class="tl-step-type">{{ typeLabel(step.node_type) }}</span>
            </div>
            <div class="tl-content-meta">
              <span class="tl-time" v-if="step.executed_at">
                <i class="bx bx-check"></i> {{ formatTime(step.executed_at) }}
              </span>
              <span class="tl-time" v-else-if="step.scheduled_at">
                <i class="bx bx-time"></i> Agendado: {{ formatTime(step.scheduled_at) }}
              </span>
              <span class="tl-time" v-else-if="step.created_at">
                <i class="bx bx-log-in"></i> {{ formatTime(step.created_at) }}
              </span>
              <span class="tl-duration" v-if="step.executed_at && step.created_at">
                {{ durationLabel(step.created_at, step.executed_at) }}
              </span>
            </div>
            <!-- Condition result -->
            <div class="tl-condition-result" v-if="step.condition_result">
              <span class="tl-cr-badge" :class="step.condition_result === 'yes' ? 'tl-cr-yes' : 'tl-cr-no'">
                <i :class="step.condition_result === 'yes' ? 'bx bx-check-circle' : 'bx bx-x-circle'"></i>
                {{ step.condition_result === 'yes' ? 'Sim' : 'Nao' }}
              </span>
            </div>
            <!-- Branch indicator -->
            <div class="tl-branch-tag" v-if="step.parent_node_id">
              <i :class="step.branch_type === 'yes' ? 'bx bx-check-circle' : 'bx bx-x-circle'"></i>
              Branch: {{ step.branch_type === 'yes' ? 'Sim' : 'Nao' }}
            </div>
            <!-- Error -->
            <div class="tl-error" v-if="step.error_message">
              <i class="bx bx-error"></i> {{ step.error_message }}
            </div>
          </div>
        </div>

        <!-- Pending nodes (not yet reached) -->
        <div
          v-for="(pNode, idx) in visiblePendingNodes"
          :key="'pending-' + pNode.node_id"
          class="tl-item tl-item-future"
          :class="{
            'tl-item-branch': !!pNode.parent_node_id,
            'tl-item-last': idx === visiblePendingNodes.length - 1
          }"
        >
          <div class="tl-dot-line">
            <div class="tl-dot tl-dot-future">
              <i class="bx bx-radio-circle"></i>
            </div>
            <div class="tl-line" v-if="idx < visiblePendingNodes.length - 1"></div>
          </div>
          <div class="tl-content">
            <div class="tl-content-head">
              <span class="tl-step-label tl-step-future">{{ pNode.node_label }}</span>
              <span class="tl-step-type">{{ typeLabel(pNode.node_type) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Enrollment footer -->
      <div class="tl-footer" v-if="timeline.enrollment.completed_at">
        <i class="bx bx-flag"></i>
        Jornada {{ enrollmentStatusLabel(timeline.enrollment.status).toLowerCase() }} em {{ formatTime(timeline.enrollment.completed_at) }}
      </div>
    </div>

    <!-- Error -->
    <div v-else class="text-center py-5 text-muted">
      <i class="bx bx-error-circle font-size-24"></i>
      <p class="mt-2 mb-0">Nao foi possivel carregar a timeline.</p>
    </div>
  </b-modal>
</template>

<script>
import { getEnrollmentTimeline } from '../../services'

export default {
  props: {
    value: { type: Boolean, default: false },
    journeyId: { type: [Number, String], default: null },
    enrollmentId: { type: [Number, String], default: null }
  },
  data () {
    return {
      loading: false,
      timeline: null
    }
  },
  computed: {
    visible: {
      get () { return this.value },
      set (val) { this.$emit('input', val) }
    },
    visiblePendingNodes () {
      if (!this.timeline) return []
      // Only show main-flow pending nodes (skip branch nodes the participant didn't take)
      return this.timeline.pending_nodes.filter(n => !n.parent_node_id)
    }
  },
  methods: {
    async loadTimeline () {
      if (!this.journeyId || !this.enrollmentId) return
      this.loading = true
      this.timeline = null
      try {
        const res = await getEnrollmentTimeline(this.journeyId, this.enrollmentId)
        this.timeline = res.data.data || res.data
      } catch (e) {
        console.error('Failed to load timeline', e)
      } finally {
        this.loading = false
      }
    },
    enrollmentStatusLabel (status) {
      const labels = { active: 'Em andamento', completed: 'Concluida', failed: 'Falhou', exited: 'Saiu', paused: 'Pausada' }
      return labels[status] || status
    },
    typeLabel (type) {
      const labels = { trigger: 'Gatilho', delay: 'Tempo', action: 'Acao', condition: 'Condicao', control: 'Controle' }
      return labels[type] || type
    },
    stepIcon (step) {
      if (step.status === 'completed') return 'bx bx-check'
      if (step.status === 'failed') return 'bx bx-x'
      if (step.status === 'waiting' || step.status === 'pending') return 'bx bx-time-five'
      if (step.status === 'processing') return 'bx bx-loader-alt bx-spin'
      return 'bx bx-check'
    },
    formatTime (dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    durationLabel (start, end) {
      if (!start || !end) return ''
      const ms = new Date(end) - new Date(start)
      if (ms < 1000) return 'instantaneo'
      const secs = Math.floor(ms / 1000)
      if (secs < 60) return secs + 's'
      const mins = Math.floor(secs / 60)
      if (mins < 60) return mins + 'min'
      const hours = Math.floor(mins / 60)
      if (hours < 24) return hours + 'h ' + (mins % 60) + 'min'
      const days = Math.floor(hours / 24)
      return days + 'd ' + (hours % 24) + 'h'
    }
  }
}
</script>

<style scoped>
/* ── Header ── */
.tl-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8f9fb;
  border-radius: 8px;
  margin-bottom: 20px;
}
.tl-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eff6ff;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.tl-header-info { flex: 1; min-width: 0; }
.tl-header-name { display: block; font-weight: 600; font-size: 14px; color: #15181e; }
.tl-header-email { display: block; font-size: 12px; color: #9aa1b2; }
.tl-enrollment-status {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.tl-es-active { background: #eff6ff; color: #3b82f6; }
.tl-es-completed { background: #ecfdf5; color: #059669; }
.tl-es-failed { background: #fef2f2; color: #ef4444; }
.tl-es-exited { background: #fffbeb; color: #d97706; }

/* ── Timeline ── */
.tl-timeline {
  padding-left: 4px;
}
.tl-item {
  display: flex;
  gap: 12px;
  min-height: 60px;
}
.tl-item-branch {
  padding-left: 24px;
}
.tl-dot-line {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 28px;
  flex-shrink: 0;
}
.tl-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  z-index: 1;
}
.tl-dot-completed { background: #ecfdf5; color: #059669; }
.tl-dot-failed { background: #fef2f2; color: #ef4444; }
.tl-dot-waiting,
.tl-dot-pending { background: #fffbeb; color: #d97706; }
.tl-dot-processing { background: #eff6ff; color: #3b82f6; }
.tl-dot-future { background: #f1f3f6; color: #9aa1b2; }
.tl-line {
  width: 2px;
  flex: 1;
  background: #e2e5eb;
  min-height: 16px;
}
.tl-item-last .tl-line { display: none; }

/* ── Content ── */
.tl-content {
  flex: 1;
  padding-bottom: 16px;
  min-width: 0;
}
.tl-content-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}
.tl-step-label {
  font-size: 13px;
  font-weight: 600;
  color: #15181e;
}
.tl-step-future {
  color: #9aa1b2;
}
.tl-step-type {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #9aa1b2;
  background: #f1f3f6;
  padding: 1px 6px;
  border-radius: 3px;
}
.tl-content-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 2px;
}
.tl-time {
  font-size: 11px;
  color: #5b6478;
  display: flex;
  align-items: center;
  gap: 3px;
}
.tl-time i { font-size: 12px; }
.tl-duration {
  font-size: 10px;
  color: #9aa1b2;
  background: #f1f3f6;
  padding: 1px 6px;
  border-radius: 3px;
}

/* Condition result */
.tl-condition-result { margin-top: 4px; }
.tl-cr-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}
.tl-cr-yes { background: #ecfdf5; color: #059669; }
.tl-cr-no { background: #fef2f2; color: #ef4444; }

/* Branch tag */
.tl-branch-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #3b82f6;
  margin-top: 3px;
}

/* Error */
.tl-error {
  margin-top: 4px;
  font-size: 11px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Footer ── */
.tl-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  margin-top: 8px;
  background: #f8f9fb;
  border-radius: 8px;
  font-size: 12px;
  color: #5b6478;
}
.tl-footer i { font-size: 14px; }
</style>
